import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    phone: '',
    email: '',
    cleaning_type: '',
    date: '',
    special_message: ''
  });

  const cleaningTypes = [
    'Residential Cleaning',
    'Commercial Cleaning',
    'Office Cleaning',
    'Carpet Cleaning',
    'Deep Cleaning',
    'Move In/Out Cleaning'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      toast.success('Thank you! We\'ll contact you shortly with a free quote.');
      setFormData({ name: '', location: '', phone: '', email: '', cleaning_type: '', date: '', special_message: '' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit request. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="header">
        {/* Top Contact Bar */}
        <div className="header-top">
          <div className="container">
            <div className="header-top-content">
              <a href="tel:1300123456" className="contact-item">
                <Phone size={16} />
                <span>1300 123 456</span>
              </a>
              <a href="mailto:info@capitalelitecleaning.com.au" className="contact-item">
                <Mail size={16} />
                <span>info@capitalelitecleaning.com.au</span>
              </a>
            </div>
          </div>
        </div>
        
        {/* Main Navigation Bar */}
        <div className="header-main">
          <div className="container">
            <div className="header-main-content">
              <img 
                src="https://customer-assets.emergentagent.com/job_3e8f6982-05b2-47e6-848f-33d6ad405bf8/artifacts/zdc1xlcb_capitalelite%20logo.png" 
                alt="Capital Elite Cleaning" 
                className="logo"
              />
              <nav className="header-nav">
                <a href="#home">Home</a>
                <a href="#services">Services</a>
                <a href="#about">About Us</a>
                <a href="#reviews">Reviews</a>
                <a href="#contact">Contact</a>
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section hero-bg-1">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Professional Cleaning Services You Can Trust</h1>
            <p className="hero-subtitle">Expert cleaning solutions for homes, offices, and businesses.</p>
            <div className="hero-buttons">
              <Link to="/book">
                <Button className="hero-cta">Book Now</Button>
              </Link>
              <Button className="hero-cta-secondary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                Get a Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive Cleaning Solutions for Every Need</p>
          </div>
          <div className="services-grid">
            <Card className="service-card">
              <div className="service-image">
                <img src="https://images.pexels.com/photos/4108713/pexels-photo-4108713.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Residential Cleaning" />
              </div>
              <CardContent className="service-content">
                <h3>Residential Cleaning</h3>
                <p>Keep your home spotless with our professional residential cleaning services tailored to your needs.</p>
                <Button variant="outline" className="service-btn">Learn More</Button>
              </CardContent>
            </Card>

            <Card className="service-card">
              <div className="service-image">
                <img src="https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBjbGVhbmluZ3xlbnwwfHx8fDE3NzM3MzQwOTB8MA&ixlib=rb-4.1.0&q=85" alt="Commercial Cleaning" />
              </div>
              <CardContent className="service-content">
                <h3>Commercial Cleaning</h3>
                <p>Professional cleaning services for offices and commercial spaces, ensuring a pristine workplace.</p>
                <Button variant="outline" className="service-btn">Learn More</Button>
              </CardContent>
            </Card>

            <Card className="service-card">
              <div className="service-image">
                <img src="https://images.pexels.com/photos/257894/pexels-photo-257894.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Office Cleaning" />
              </div>
              <CardContent className="service-content">
                <h3>Office Cleaning</h3>
                <p>Reliable and thorough office cleaning services to maintain a healthy, productive workspace.</p>
                <Button variant="outline" className="service-btn">Learn More</Button>
              </CardContent>
            </Card>

            <Card className="service-card">
              <div className="service-image">
                <img src="https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjbGVhbmluZyUyMHNlcnZpY2V8ZW58MHx8fHwxNzczNzM0MTEzfDA&ixlib=rb-4.1.0&q=85" alt="Carpet Cleaning" />
              </div>
              <CardContent className="service-content">
                <h3>Carpet Cleaning</h3>
                <p>Deep carpet cleaning services using eco-friendly products to remove stains and refresh your carpets.</p>
                <Button variant="outline" className="service-btn">Learn More</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1647381518264-97ff1835026f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGNsZWFuaW5nfGVufDB8fHx8MTc3MzczNDA4M3ww&ixlib=rb-4.1.0&q=85" alt="About Capital Elite Cleaning" />
            </div>
            <div className="about-content">
              <h2>About Us</h2>
              <p className="about-subtitle">Your Trusted Cleaning Experts</p>
              <p className="about-text">
                Capital Elite Cleaning is dedicated to providing top-quality, reliable cleaning services throughout Canberra. With years of experience, our fully trained and insured team is committed to excellence and customer satisfaction. We use eco-friendly products and the latest equipment to ensure your space is sparkling clean.
              </p>
              <div className="about-features">
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Fully Insured & Trained Staff</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Eco-Friendly Products</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>100% Satisfaction Guarantee</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Serving Canberra & Surrounds</span>
                </div>
              </div>
              <Button className="about-cta">Learn More</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="reviews-section">
        <div className="container">
          <div className="section-header">
            <h2>Customer Reviews</h2>
            <p>What Our Clients Say</p>
          </div>
          <div className="reviews-grid">
            <Card className="review-card">
              <CardContent className="review-content">
                <div className="stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#FFD700" color="#FFD700" />)}
                </div>
                <p className="review-text">
                  CapitalEliteCleaning provided exceptional service. My home has never looked better!
                </p>
                <p className="review-author">Sarah M.</p>
              </CardContent>
            </Card>

            <Card className="review-card">
              <CardContent className="review-content">
                <div className="stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#FFD700" color="#FFD700" />)}
                </div>
                <p className="review-text">
                  Fantastic service! Very professional and great attention to detail.
                </p>
                <p className="review-author">James R.</p>
              </CardContent>
            </Card>

            <Card className="review-card">
              <CardContent className="review-content">
                <div className="stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#FFD700" color="#FFD700" />)}
                </div>
                <p className="review-text">
                  Reliable, thorough, and friendly team. Highly recommend their services!
                </p>
                <p className="review-author">Emily K.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact/Quote Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-box">
              <h2>Get a Free Quote</h2>
              <p className="contact-subtitle">Contact Us For a Free, No-Obligation Quote</p>
              
              <div className="contact-details">
                <div className="detail-item">
                  <Phone className="detail-icon" />
                  <div>
                    <h4>Phone</h4>
                    <p>1300 123 456</p>
                  </div>
                </div>
                <div className="detail-item">
                  <Mail className="detail-icon" />
                  <div>
                    <h4>Email</h4>
                    <p>info@capitalelitecleaning.com.au</p>
                  </div>
                </div>
                <div className="detail-item">
                  <MapPin className="detail-icon" />
                  <div>
                    <h4>Location</h4>
                    <p>Canberra, ACT, Australia</p>
                  </div>
                </div>
                <div className="detail-item">
                  <Clock className="detail-icon" />
                  <div>
                    <h4>Hours</h4>
                    <p>Mon-Fri: 7AM - 6PM<br />Sat: 8AM - 4PM</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="contact-form-card">
              <CardContent className="form-content">
                <form onSubmit={handleSubmit}>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    type="text"
                    name="location"
                    placeholder="Location/Address"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    type="text"
                    name="cleaning_type"
                    placeholder="Cleaning Type (e.g., Residential, Office)"
                    value={formData.cleaning_type}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                  <Input
                    type="date"
                    name="date"
                    placeholder="Preferred Date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="form-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <Textarea
                    name="special_message"
                    placeholder="Special Requests or Message"
                    value={formData.special_message}
                    onChange={handleChange}
                    className="form-textarea"
                    rows={4}
                  />
                  <Button type="submit" className="submit-btn">Get Free Quote</Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <img 
                src="https://customer-assets.emergentagent.com/job_3e8f6982-05b2-47e6-848f-33d6ad405bf8/artifacts/zdc1xlcb_capitalelite%20logo.png" 
                alt="Capital Elite Cleaning" 
                className="footer-logo"
              />
              <p>Professional cleaning services in Canberra. Trusted by homes and businesses.</p>
            </div>
            <div className="footer-column">
              <h4>Quick Links</h4>
              <a href="#home">Home</a>
              <a href="#services">Services</a>
              <a href="#about">About Us</a>
              <a href="#reviews">Reviews</a>
            </div>
            <div className="footer-column">
              <h4>Services</h4>
              <a href="#services">Residential Cleaning</a>
              <a href="#services">Commercial Cleaning</a>
              <a href="#services">Office Cleaning</a>
              <a href="#services">Carpet Cleaning</a>
            </div>
            <div className="footer-column">
              <h4>Contact</h4>
              <p><Phone size={16} /> 1300 123 456</p>
              <p><Mail size={16} /> info@capitalelitecleaning.com.au</p>
              <p><MapPin size={16} /> Canberra, ACT</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Capital Elite Cleaning. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
